import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import PreferenceTable from '../components/PreferenceTable';
import { useTablePreferences } from '../hooks/useTablePreferences';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, 
  Search, 
  MapPin, 
  Calendar,
  Filter,
  X,
  LayoutGrid,
  TableIcon,
  Edit,
  Copy,
  Wrench,
  AlertTriangle
} from 'lucide-react';
import MapButtons from '../components/MapButtons';
import { formatDate, getStatusColor, getStatusStyle, getPriorityColor, parseLocalDate, getContrastTextColor } from '../lib/utils';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Jobs() {
  const { isAdmin, hasPermission } = useAuth();
  const navigate = useNavigate();
  // Pre-seed status/priority/team filters from the URL so deep links from
  // the Dashboard banners (e.g. /jobs?priority=Urgent or /jobs?status=Pending)
  // actually land on the filtered view instead of the raw full list.
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || '');
  const [priorityFilter, setPriorityFilter] = useState(() => searchParams.get('priority') || '');
  const [serviceFilter, setServiceFilter] = useState(() => searchParams.get('service_type') || '');
  const [services, setServices] = useState([]);
  const [team, setTeam] = useState([]);
  const [teamFilter, setTeamFilter] = useState(() => searchParams.get('team_member') || '');
  const [jobStatuses, setJobStatuses] = useState([]);
  const [statusCounts, setStatusCounts] = useState({ total: 0, by_status: {} });
  
  // View mode: 'table' or 'cards' - default to table, persist in localStorage
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('jobsViewMode') || 'table';
  });
  
  // Sorting state
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Pagination state — Jobs table is server-paginated at 100/page
  const [page, setPage] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  
  // Mirror current filter state into the URL so the page is shareable and
  // browser-back works. Avoids stomping unrelated params on the URL.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const sync = (key, value) => {
      if (value) next.set(key, value);
      else next.delete(key);
    };
    sync('status', statusFilter);
    sync('priority', priorityFilter);
    sync('service_type', serviceFilter);
    sync('team_member', teamFilter);
    sync('search', search);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, priorityFilter, serviceFilter, teamFilter, search]);

  // Update localStorage when view mode changes
  useEffect(() => {
    localStorage.setItem('jobsViewMode', viewMode);
  }, [viewMode]);

  const fetchJobs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (priorityFilter) params.append('priority', priorityFilter);
      if (serviceFilter) params.append('service_type', serviceFilter);
      if (teamFilter) params.append('team_member', teamFilter);
      if (search) params.append('search', search);
      params.append('page', page);
      params.append('sort_by', sortBy);
      params.append('sort_dir', sortOrder);
      
      const response = await axios.get(`${API}/jobs?${params.toString()}`);
      const items = response.data.items || [];
      const total = response.data.total || 0;
      setJobs(items);
      setTotalJobs(total);
      // Current page is past the end (e.g. a filter/search shrank the
      // result set) — snap back to the last valid page instead of
      // showing an empty page the user can't navigate out of.
      if (items.length === 0 && total > 0) {
        const lastPage = Math.max(1, Math.ceil(total / 100));
        if (lastPage !== page) {
          setPage(lastPage);
        }
      }
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, serviceFilter, teamFilter, search, page, sortBy, sortOrder]);

  const fetchReferenceData = async () => {
    try {
      const response = await axios.get(`${API}/reference-data`);
      const data = response.data;
      setServices(data.services || []);
      setTeam(data.team || []);
      setJobStatuses((data.job_statuses || []).filter(s => s.active));
    } catch (error) {
      console.error('Failed to fetch reference data:', error);
    }
  };

  const fetchStatusCounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/jobs/status-counts`);
      setStatusCounts(res.data || { total: 0, by_status: {} });
    } catch (error) {
      console.error('Failed to fetch status counts:', error);
    }
  }, []);

  const handleDuplicate = async (job, withFiles = true) => {
    try {
      // Shallow-clone file/photo arrays so the duplicate has its own DB
      // records but shares the underlying OneDrive/Cloudinary blobs.
      // We regenerate `id` per record to avoid collisions, but keep `url`,
      // `public_id`, and `storage` so previews work immediately on both jobs.
      const cloneFile = (f) => ({
        ...f,
        id: (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      });
      const cloneList = (arr) => (withFiles && Array.isArray(arr)) ? arr.map(cloneFile) : [];

      const payload = {
        ...job,
        job_title: `${job.job_title || ''} - Day 2`,
        date: job.date,
        end_date: job.end_date || '',
        files: cloneList(job.files),
        photos: cloneList(job.photos),
        tickets_811_files: cloneList(job.tickets_811_files),
        permit_files: cloneList(job.permit_files),
      };
      delete payload.id;
      delete payload.job_number;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.job_days;
      delete payload.is_multi_day;

      const res = await axios.post(`${API}/jobs?source=duplicate`, payload);
      const totalFiles = (payload.files?.length || 0)
        + (payload.photos?.length || 0)
        + (payload.tickets_811_files?.length || 0)
        + (payload.permit_files?.length || 0);
      toast.success(
        totalFiles > 0
          ? `Job duplicated: ${res.data.job_number} (${totalFiles} file${totalFiles === 1 ? '' : 's'} linked)`
          : `Job duplicated: ${res.data.job_number}`
      );
      fetchStatusCounts();
      navigate(`/jobs/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to duplicate job');
    }
  };

  // Pending job awaiting the duplicate-confirmation dialog choice.
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const openDuplicateDialog = (job) => setDuplicateTarget(job);
  const confirmDuplicate = async (withFiles) => {
    const target = duplicateTarget;
    setDuplicateTarget(null);
    if (target) await handleDuplicate(target, withFiles);
  };



  useEffect(() => {
    fetchJobs();
    fetchReferenceData();
    fetchStatusCounts();
  }, [fetchJobs, fetchStatusCounts]);

  // Auto-refresh when user returns to app — list only; counts only refresh on mount + after job CRUD
  useAutoRefresh(fetchJobs, { refreshOnReturn: true });

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchJobs();
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setPriorityFilter('');
    setServiceFilter('');
    setTeamFilter('');
    setPage(1);
  };

  const hasActiveFilters = search || statusFilter || priorityFilter || serviceFilter || teamFilter;

  // Sort function — re-fetches from the server, which sorts across the full job list
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(1);
  };

  // Quick status change handler
  const handleStatusChange = async (job, newStatus) => {
    try {
      await axios.put(`${API}/jobs/${job.id}`, { ...job, status: newStatus });
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  // Column config for PreferenceTable — mirrors the previous hard-coded table.
  const jobColumns = [
    { id: 'job_number', label: 'Job #', sortKey: 'job_number', defaultWidth: 130,
      render: (job) => <span className="font-mono text-amber-600 font-bold text-sm">{job.job_number}</span> },
    { id: 'client_name', label: 'Client', sortKey: 'client_name', defaultWidth: 170,
      render: (job) => <span className="truncate block">{job.client_name || '—'}</span> },
    { id: 'job_title', label: 'Job Title', sortKey: 'job_title', defaultWidth: 220,
      cellClassName: 'font-medium',
      render: (job) => <span className="truncate block">{job.job_title || '—'}</span> },
    { id: 'service_type', label: 'Service', sortKey: 'service_type', defaultWidth: 140,
      render: (job) => <Badge variant="outline" className="font-normal">{job.service_type}</Badge> },
    { id: 'status', label: 'Status', sortKey: 'status', defaultWidth: 160,
      render: (job) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Select
            value={job.status}
            onValueChange={(newStatus) => handleStatusChange(job, newStatus)}
          >
            <SelectTrigger className={`w-[130px] h-8 ${getStatusColor(job.status)}`} style={getStatusStyle(job.status) || undefined} data-testid={`job-table-status-select-${job.id}`}>
              <SelectValue>{job.status}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {jobStatuses.map(s => (
                <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) },
    { id: 'priority', label: 'Priority', sortKey: 'priority', defaultWidth: 120,
      render: (job) => <Badge className={getPriorityColor(job.priority)}>{job.priority}</Badge> },
    { id: 'date', label: 'Job Date', sortKey: 'date', defaultWidth: 170,
      cellClassName: 'text-sm',
      render: (job) => (
        <>
          {job.date ? format(parseLocalDate(job.date), 'MMM d, yyyy') : '—'}
          {job.time && <span className="text-slate-400 ml-1">{job.time}</span>}
        </>
      ) },
    { id: 'team', label: 'Team', defaultWidth: 140,
      render: (job) => job.assigned_team_members?.length > 0 ? (
        <div className="flex -space-x-2">
          {job.assigned_team_members.slice(0, 3).map((memberId, idx) => {
            const member = team.find(t => t.id === memberId);
            const bgColor = member?.calendar_color || '#f59e0b';
            return (
              <div
                key={idx}
                className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold shadow-sm"
                style={{ backgroundColor: bgColor, color: getContrastTextColor(bgColor) }}
                title={member?.full_name}
              >
                {member?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
              </div>
            );
          })}
          {job.assigned_team_members.length > 3 && (
            <div className="w-6 h-6 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600">
              +{job.assigned_team_members.length - 3}
            </div>
          )}
        </div>
      ) : null },
    { id: 'equipment', label: 'Equipment', defaultWidth: 200, defaultHidden: true,
      render: (job) => job.assigned_equipment_ids?.length > 0 ? (
        <span className="truncate block text-slate-600">
          {job.assigned_equipment_ids.map(eid => (job.assigned_equipment_snapshot || {})[eid] || eid).join(', ')}
        </span>
      ) : null },
    { id: 'actions', label: 'Actions', defaultWidth: 180, alignRight: true,
      render: (job) => (
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <MapButtons
            address={job.service_address}
            showLabels={false}
            variant="ghost"
          />
          <Button variant="ghost" size="sm" onClick={() => navigate(`/jobs/${job.id}`)}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => openDuplicateDialog(job)} title="Duplicate Job">
            <Copy className="w-4 h-4" />
          </Button>
        </div>
      ) },
  ];
  const jobsPrefs = useTablePreferences('jobs', jobColumns);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-32 bg-slate-200 animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="h-48 bg-slate-200 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="jobs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-tight">
          Jobs
        </h1>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex border rounded-md">
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
              className={viewMode === 'table' ? 'bg-slate-800 text-white' : ''}
            >
              <TableIcon className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'cards' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('cards')}
              className={viewMode === 'cards' ? 'bg-slate-800 text-white' : ''}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>
          {(isAdmin() || hasPermission('create_jobs')) && (
            <Link to="/jobs/new">
              <Button size="sm" className="h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold" data-testid="new-job-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Job
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search jobs..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="pl-10 bg-slate-50 border-slate-200"
                  data-testid="job-search-input"
                />
              </div>
              <Button type="submit" variant="outline" className="border-slate-200">
                Search
              </Button>
            </form>
          </div>

          <div className="flex flex-wrap gap-3 mt-4">
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[150px] bg-slate-50" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {jobStatuses.map(status => (
                  <SelectItem key={status.id} value={status.name}>{status.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={priorityFilter} onValueChange={(v) => { setPriorityFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[150px] bg-slate-50" data-testid="priority-filter">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
                <SelectItem value="Normal">Normal</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>

            <Select value={serviceFilter} onValueChange={(v) => { setServiceFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[180px] bg-slate-50" data-testid="service-filter">
                <SelectValue placeholder="Service Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                {services.map(service => (
                  <SelectItem key={service.id} value={service.name}>{service.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={teamFilter} onValueChange={(v) => { setTeamFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[180px] bg-slate-50" data-testid="team-filter">
                <SelectValue placeholder="Team Member" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Team</SelectItem>
                {team.map(member => (
                  <SelectItem key={member.id} value={member.id}>{member.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button variant="ghost" onClick={clearFilters} className="text-slate-500">
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Status Tiles */}
      {jobStatuses.length > 0 && (() => {
        const visibleStatuses = jobStatuses.filter(s => (s.name || '').toLowerCase() !== 'cancelled');
        const tileCount = visibleStatuses.length + 1; // +1 for "All Jobs"
        // Tailwind needs literal class names — map the count to a fixed lg-column class.
        const lgColsClass = {
          1: 'lg:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4',
          5: 'lg:grid-cols-5', 6: 'lg:grid-cols-6', 7: 'lg:grid-cols-7', 8: 'lg:grid-cols-8',
          9: 'lg:grid-cols-9', 10: 'lg:grid-cols-10', 11: 'lg:grid-cols-11', 12: 'lg:grid-cols-12',
        }[Math.min(tileCount, 12)] || 'lg:grid-cols-6';
        return (
          <div
            className={`grid grid-cols-2 sm:grid-cols-3 ${lgColsClass} gap-3`}
            data-testid="job-status-tiles"
          >
            <Card
              className={`border-slate-200 cursor-pointer hover:border-amber-500/50 transition-all ${statusFilter === '' ? 'ring-2 ring-amber-500' : ''}`}
              onClick={() => { setStatusFilter(''); setPage(1); }}
              data-testid="status-tile-all"
            >
              <CardContent className="p-3 text-center">
                <div className="text-2xl font-bold text-slate-900">{statusCounts.total}</div>
                <div className="text-xs text-slate-500">All Jobs</div>
              </CardContent>
            </Card>
            {visibleStatuses.map((status) => {
              const count = statusCounts.by_status?.[status.name] || 0;
              const isActive = statusFilter === status.name;
              return (
                <Card
                  key={status.id}
                  className={`border-slate-200 cursor-pointer hover:border-amber-500/50 transition-all ${isActive ? 'ring-2 ring-amber-500' : ''}`}
                  onClick={() => { setStatusFilter(isActive ? '' : status.name); setPage(1); }}
                  data-testid={`status-tile-${status.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <CardContent className="p-3 text-center">
                    <div className="text-2xl font-bold text-slate-900">{count}</div>
                    <div className="text-xs text-slate-500 truncate" title={status.name}>{status.name}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        );
      })()}

      {/* Jobs Grid/Table */}
      {jobs.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Filter className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-900 mb-1">No jobs found</h3>
            <p className="text-slate-500">Try adjusting your filters or create a new job</p>
          </CardContent>
        </Card>
      ) : viewMode === 'table' ? (
        /* Table View — resize, reorder, hide columns are per-user prefs */
        <Card className="border-slate-200">
          <div className="p-3">
            <PreferenceTable
              columns={jobColumns}
              rows={jobs}
              rowKey={(j) => j.id}
              prefs={jobsPrefs}
              sortBy={sortBy}
              sortDir={sortOrder}
              onSort={(col) => handleSort(col.sortKey)}
              onRowClick={(job) => navigate(`/jobs/${job.id}`)}
              rowClassName={() => 'cursor-pointer hover:bg-slate-50'}
              emptyMessage="No jobs match your filters."
              data-testid="jobs-table"
            />
          </div>
        </Card>
      ) : (
        /* Card View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {jobs.map(job => (
            <Card 
              key={job.id} 
              className="border-slate-200 hover:border-amber-500/50 transition-all cursor-pointer job-card"
              onClick={() => navigate(`/jobs/${job.id}`)}
              data-testid={`job-card-${job.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    {job.job_number && (
                      <span className="text-xs font-mono text-amber-600 bg-amber-50 px-2 py-0.5 rounded mb-1 inline-block">{job.job_number}</span>
                    )}
                    <h3 className="font-semibold text-slate-900 line-clamp-1">{job.job_title}</h3>
                    <p className="text-sm text-slate-500">{job.client_name}</p>
                  </div>
                  <Badge className={getPriorityColor(job.priority)}>{job.priority}</Badge>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Badge variant="outline" className="font-normal">{job.service_type}</Badge>
                  </div>
                  
                  <div className="flex items-center gap-2 text-slate-500">
                    {job.service_street ? (
                      <>
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span className="line-clamp-1">{job.service_address}</span>
                      </>
                    ) : (
                      <Badge className="bg-red-100 text-red-700 hover:bg-red-100 border-red-200 font-normal" data-testid={`job-card-address-needed-${job.id}`}>
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Site address needed
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 text-slate-500">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(job.date)}</span>
                    {job.time && <span>at {job.time}</span>}
                  </div>

                  {job.assigned_equipment_ids?.length > 0 && (
                    <div className="flex items-start gap-2 text-slate-500" data-testid={`job-card-equipment-${job.id}`}>
                      <Wrench className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span className="line-clamp-1">
                        {job.assigned_equipment_ids.map(eid => (job.assigned_equipment_snapshot || {})[eid] || eid).join(', ')}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-2">
                    <Badge className={getStatusColor(job.status)} style={getStatusStyle(job.status) || undefined}>{job.status}</Badge>
                    <MapButtons 
                      address={job.service_address} 
                      showLabels={false}
                      variant="ghost"
                    />
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); openDuplicateDialog(job); }} title="Duplicate Job">
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  {job.assigned_team_members?.length > 0 && (
                    <div className="flex -space-x-2">
                      {job.assigned_team_members.slice(0, 3).map((memberId, idx) => {
                        const member = team.find(t => t.id === memberId);
                        const bgColor = member?.calendar_color || '#f59e0b';
                        return (
                          <div 
                            key={idx}
                            className="w-7 h-7 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold shadow-sm"
                            style={{ backgroundColor: bgColor, color: getContrastTextColor(bgColor) }}
                            title={member?.full_name}
                          >
                            {member?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                          </div>
                        );
                      })}
                      {job.assigned_team_members.length > 3 && (
                        <div className="w-7 h-7 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600">
                          +{job.assigned_team_members.length - 3}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination — Jobs table is server-paginated at 100/page */}
      {(jobs.length > 0 || page > 1) && (
        <div className="flex items-center justify-between" data-testid="jobs-pagination">
          <Button
            variant="outline"
            size="sm"
            className="border-slate-200"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            data-testid="jobs-prev-page-btn"
          >
            Prev
          </Button>
          <span className="text-sm text-slate-500" data-testid="jobs-page-indicator">
            Page {page} of {Math.max(1, Math.ceil(totalJobs / 100))}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="border-slate-200"
            onClick={() => setPage(p => (p < Math.ceil(totalJobs / 100) ? p + 1 : p))}
            disabled={page >= Math.ceil(totalJobs / 100)}
            data-testid="jobs-next-page-btn"
          >
            Next
          </Button>
        </div>
      )}

      {/* Duplicate Job confirmation dialog */}
      <Dialog open={!!duplicateTarget} onOpenChange={(o) => { if (!o) setDuplicateTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="duplicate-job-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed']">Duplicate Job</DialogTitle>
            <DialogDescription className="pt-2 space-y-2 text-slate-700">
              <span className="block">
                Duplicate files along with the job?
              </span>
              <span className="block text-sm text-slate-500">
                A new job number will be created. Files will be{' '}
                <strong className="text-slate-700">linked</strong> to the new job
                (same underlying file — deleting from one job removes the link
                on both).
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setDuplicateTarget(null)}
              data-testid="duplicate-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => confirmDuplicate(false)}
              data-testid="duplicate-without-files-btn"
            >
              Without files
            </Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
              onClick={() => confirmDuplicate(true)}
              data-testid="duplicate-with-files-btn"
            >
              <Copy className="w-4 h-4 mr-1" /> Duplicate with files
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
